import { useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import pushRoute from '@utils/pushRoute';
import useSelectedGoal, {
  useSelectedGoalUID
} from '@api/hooks/topology/useSelectedGoal';
import {
  trackCheckoutPrimeStarted,
  trackPrimePitchDismissed,
  trackPrimePitchDisplayed
} from '@stores/prime-pitch/primePitchAnalytics';
import { resolveDismissNavigation } from './dismissNavigation';
import PrimeTrialPromoModal from './PrimeTrialPromoModal';
import usePrimeTrialPromoModal from './usePrimeTrialPromoModal';
import usePrimeTrialPromoModalStore from './store';

export default function PrimeTrialPromoModalRoot() {
  const router = useRouter();
  const selectedGoalUID = useSelectedGoalUID();
  const { selectedGoal } = useSelectedGoal();
  const selectedGoalSlug = selectedGoal?.goalSlug || selectedGoal?.slug;
  const selectedGoalName = selectedGoal?.name;
  const { open, loading, error, data, closePrimeTrialPromoModal, joinPrime } =
    usePrimeTrialPromoModal();
  const dismissNavigateTo = usePrimeTrialPromoModalStore(
    (state) => state.dismissNavigateTo
  );
  const onDismiss = usePrimeTrialPromoModalStore((state) => state.onDismiss);
  const onJoinPrimeCallback = usePrimeTrialPromoModalStore(
    (state) => state.onJoinPrime
  );
  const modalGoalSlug = usePrimeTrialPromoModalStore((state) => state.goalSlug);
  const contextId = usePrimeTrialPromoModalStore((state) => state.contextId);
  const analyticsContext = usePrimeTrialPromoModalStore(
    (state) => state.analyticsContext
  );
  const subscriptionPrice = usePrimeTrialPromoModalStore(
    (state) => state.subscriptionPrice
  );
  const goalUID = usePrimeTrialPromoModalStore((state) => state.goalUID);
  const pitchDisplayedRef = useRef(false);

  useEffect(() => {
    if (open && data && !loading && !error && !pitchDisplayedRef.current) {
      pitchDisplayedRef.current = true;
      trackPrimePitchDisplayed({
        contextualFlow: contextId,
        analyticsContext: analyticsContext || undefined,
        subscriptionPrice
      });
    }

    if (!open) {
      pitchDisplayedRef.current = false;
    }
  }, [
    open,
    data,
    loading,
    error,
    contextId,
    analyticsContext,
    subscriptionPrice
  ]);

  const handleClose = () => {
    trackPrimePitchDismissed({
      contextualFlow: contextId,
      analyticsContext: analyticsContext || undefined,
      subscriptionPrice
    });

    onDismiss?.();
    closePrimeTrialPromoModal();

    const navigation = resolveDismissNavigation({
      dismissNavigateTo,
      goalSlug: modalGoalSlug,
      goalUID,
      fallbackGoalSlug: selectedGoalSlug,
      fallbackGoalUID: selectedGoalUID
    });

    if (navigation === 'stay' || !navigation) {
      return;
    }

    if (navigation === 'back') {
      router.back();
      return;
    }

    router.replace(navigation);
  };

  const handleJoinPrime = () => {
    trackCheckoutPrimeStarted({
      contextualFlow: contextId,
      analyticsContext: analyticsContext || undefined,
      goalUid: goalUID || selectedGoalUID || undefined,
      goalName: selectedGoalName,
      subscriptionPrice
    });
    onJoinPrimeCallback?.();
    joinPrime();
  };

  if (!open || loading || error || !data) {
    return null;
  }

  return (
    <PrimeTrialPromoModal
      open={open}
      onClose={handleClose}
      onJoinPrime={handleJoinPrime}
      heroVideoUrl={data.heroVideoUrl}
      brandLogoSrc={data.brandLogoSrc}
      primeLogoSrc={data.primeLogoSrc}
      priceImageSrc={data.priceImageSrc}
      headline={data.headline}
      thenLine={data.thenLine}
      primaryCtaLabel={data.primaryCtaLabel}
      benefits={data.benefits}
    />
  );
}
